import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.4/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.3/js/sparta-global-functions-utility";
import _ from "utilities/vendor/lodash/4.17.21/js/lodash.custom.min";
import spartaMediaQueryUtility from "utilities/global/sparta-mediaquery-utility/2.0.4/js/sparta-mediaquery-utility";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import spartaStyleUtility from "utilities/global/sparta-style-utility/3.2.4/js/sparta-style-utility";
import breakOut from "utilities/global/sparta-breakout-utility/2.0.1/js/sparta-breakout-utility";
/* global _ breakOut spartaEventsUtility spartaStoreUtility */

/**
 * Namespace for the Sparta Accordion Utility.
 * @constant {string}
 */
const utilityNamespace = 'spartaAccordionUtility';

/**
 * Allow innerPanel event listener to fire first time and prevent on additional
 */
let instance = {};

/**
 * Collection of namespaced event strings.
 * @constant {object}
 */
const EVENTS = {
  ACCORDION_INITIALIZED: `ACCORDION_INITIALIZED.${utilityNamespace}`,
  ACCORDION_ITEM_HIDE: `ACCORDION_ITEM_HIDE.${utilityNamespace}`,
  ACCORDION_ITEM_SHOW: `ACCORDION_ITEM_SHOW.${utilityNamespace}`,
  ACCORDION_SHOW_ALL: `ACCORDION_SHOW_ALL.${utilityNamespace}`,
  ACCORDION_HIDE_ALL: `ACCORDION_HIDE_ALL.${utilityNamespace}`,
};

/**
 * Collection of numeric keycodes for input handlers.
 * @constant {object}
 */
const KEYS = {
  ENTER: 13,
  SPACE: 32,
  UP: 38,
  DOWN: 40,
};

/**
 * Collection of possible messages to show the user.
 * @constant {object}
 */
const STRINGS = {
  NO_PANEL_CONTENT_ERROR: '<strong>Error:</strong> No panel content available.',
};

/**
 * The icon types that are currently supported for the accordion headers/tabs.
 * @constant {array}
 */
const SUPPORTED_STATUS_ICONS = ['error', 'warning', 'success'];

/**
 * The length of panel open/close animations in milliseconds.
 * @constant {number}
 */
const PANEL_ANIMATION_LENGTH = 300;

/**
 * Extra spacing to add (in pixels) when scrolling an item into view.
 * @constant {number}
 */
const SCROLL_OFFSET_PADDING = 10;

/**
 * Medium width breakpoint.
 * @constant {number}
 */
const MEDIUM_SCREEN = 768;

/**
 * The Accordion Utility allows large amounts of page content to be displayed in a limited area. In
 * larger viewports, the same content can optionally be shown as a set of tabs.
 */
class SpartaAccordion {
  /**
   * Finds all accordions and applies event listeners to the tabs.
   * @returns {void}
   */
  init() {
    // Initialized accordions.
    this.accordions = [];

    this.addEventHandling();

    // Activate (possibly deep-linked) accordion and scroll to its content.
    spartaEventsUtility.sub(EVENTS.ACCORDION_INITIALIZED, () => {
      setTimeout(this.activateSection.bind(this), PANEL_ANIMATION_LENGTH);
      window.addEventListener('hashchange', this.activateSection.bind(this), false);
      window.addEventListener('popstate', this.activateSection.bind(this), false);
    }, true);

    // Initialize language and language-specific content
    [this.language] = spartaStoreUtility.getData('lang').split('-');
    this.content = spartaStoreUtility.getData('spaParams').global[this.language].accordions;

    // Apply event listeners to each tab.
    const accordionTabs = document.querySelectorAll('.accordion-tab, .panel-header');

    Array.prototype.forEach.call(accordionTabs, (item) => {
      // Prefetch to make sure the on-click interaction is as light as possible.
      const parent = item.closest('.accordion-wrapper');
      const panelID = item.getAttribute('data-panel');
      const panel = parent.querySelector(`#${panelID}`);
      const tab = parent.querySelector(`.accordion-tab[data-panel=${panelID}]`);
      const isInitialized = item.getAttribute('data-initialized');

      // create init instance based off of parent ID
      instance[parent.id] = {};

      if (!isInitialized) {
        // Initialize active-state ADA attributes and hidden text.
        const panelIsActive = panel.classList.contains('active');
        this.setActiveState(panel, panelIsActive);
        this.setActiveState(tab, panelIsActive);

        // Check for dynamic module content and move it to the panel.
        this.getModuleContent(panel);

        // This is needed to keep the old open/closed state text from being read on click.
        item.addEventListener('mousedown', () => {
          this.setHiddenStateText(panel, !panel.classList.contains('active'));
          this.setHiddenStateText(tab, true);
        });

        // Open the panel when its header or tab is clicked or keyboard-activated.
        item.addEventListener('click', () => {
          this.activatePanel(item);
        });

        item.addEventListener('keydown', (event) => {
          const navPressed = this.navKeyPressed(event);
          const code = event.charCode || event.keyCode;

          // Only listen for a nav key or the down/up arrows (which specifically open or close).
          let forceState = null;

          if (code === KEYS.DOWN) {
            forceState = true;
          } else if (code === KEYS.UP) {
            forceState = false;
          }

          if (!navPressed && forceState === null) {
            return;
          }

          event.preventDefault();

          this.activatePanel(item, forceState);
        });

        // Add accordion to the this.accordions array.
        if (!this.accordions.includes(parent.getAttribute('id'))) {
          this.accordions.push(parent.getAttribute('id'));
        }

        item.setAttribute('data-initialized', true);
      }
    });

    // In the rare case that the user:
    // Closes all the accordions AND resizes from small to medium view
    // Show the first tab active if the accordion type is 'tabs'

    let accordionType;
    if (accordionTabs && accordionTabs[0]) {
      const parent = accordionTabs[0].closest('.accordion-wrapper');
      accordionType = parent.dataset.accordionType;
    }

    if (accordionTabs && accordionTabs.length && accordionType === 'tabs') {
      window.addEventListener('resize', _.throttle(() => {
        const isMediumView = window.matchMedia(`(min-width: ${MEDIUM_SCREEN}px)`).matches;
        if (isMediumView) {
          const activeTabs = Array.from(accordionTabs)
            .filter(tab => tab.classList.contains('active'));

          const activePanels = document.querySelectorAll('.accordion-panel.active');

          // Check if all tabs are closed
          if (activeTabs.length === 0 && activePanels.length === 0) {
            // Open first tab
            this.setActiveState(accordionTabs[0], true);
            this.activatePanel(accordionTabs[0]);
          }
        }
      }), 250);
    }

    // Apply event listeners to each accordion group controls link.
    const breakOutLinks = document.querySelectorAll('.spa-hidden[data-breakout]');

    Array.prototype.forEach.call(breakOutLinks, (link) => {
      link.addEventListener('click', () => {
        this.exposeAllPanels(link);
      });

      link.addEventListener('keydown', (event) => {
        if (this.navKeyPressed(event)) {
          event.preventDefault();
          this.exposeAllPanels(link);
        }
      });
    });

    const expandAllLinks = document.querySelectorAll('.accordion-expand-all-link');

    Array.prototype.forEach.call(expandAllLinks, (link) => {
      link.addEventListener('click', () => {
        this.showAllPanels(link, false);
      });

      link.addEventListener('keydown', (event) => {
        if (this.navKeyPressed(event)) {
          event.preventDefault();
          this.showAllPanels(link, false);
        }
      });
    });

    const collapseAllLinks = document.querySelectorAll('.accordion-collapse-all-link');

    Array.prototype.forEach.call(collapseAllLinks, (link) => {
      link.addEventListener('click', () => {
        this.hideAllPanels(link);
      });

      link.addEventListener('keydown', (event) => {
        if (this.navKeyPressed(event)) {
          event.preventDefault();
          this.hideAllPanels(link);
        }
      });
    });

    // Apply event listeners to each show/hide link.
    const showAllLinks = document.querySelectorAll('.show-all-text');
    Array.prototype.forEach.call(showAllLinks, (link) => {
      link.addEventListener('click', () => {
        this.togglePanels(link);
      });

      link.addEventListener('keydown', (event) => {
        if (this.navKeyPressed(event)) {
          event.preventDefault();
          this.togglePanels(link);
        }
      });
    });

    const expandCollapseControls = document.querySelectorAll('.accordion-group-control-wrapper');

    Array.prototype.forEach.call(expandCollapseControls, (accordionGroup) => {
      this.updateAccordionGroupState(accordionGroup.closest('.accordion-wrapper'));
    });

    // If utility is included, invoke prerender/breakout event listener.
    if (typeof breakOut === 'object') {
      breakOut.init();
    }

    // based on JCR and innerPanels add API to toggle innerPanels from parent level control
    if (expandCollapseControls) {
      let controlsArray = [...expandCollapseControls];
      controlsArray.forEach( (element) => {
        const wrapperElm = element.parentElement;
        const expandCollapseInnerPanels = wrapperElm.getAttribute('data-expand-collapse-inner-panels');

        // if we're not toggling innerPanels kill check
        if (expandCollapseInnerPanels !== 'true') {
          return false;
        }

        // add event listeners to control innerPanels
        this.expandCollapseInnerPanels(wrapperElm);
      });
    }

    spartaEventsUtility.pub(EVENTS.ACCORDION_INITIALIZED, this.accordions);
  }

  /**
   * Accessibility for keyboard users.
   * @param {object} event - The browser's `keydown` event.
   * @returns {boolean} An indication of whether the key pressed was enter or the spacebar.
   */
  navKeyPressed(event) {
    const code = event.charCode || event.keyCode;
    return (code === KEYS.ENTER || code === KEYS.SPACE);
  }

  /**
   * Scrolls to a given element by slug ID, accounting for sticky bar height.
   * @param {string} target - Optional slug ID for the target panel. Defaults to location hash.
   * @returns {void}
   */
  activateSection(target) {
    // Trim the supplied hash down to one or two stub ID values.
    let trimTarget = (typeof target === 'string') ? target : window.location.hash;
    if (trimTarget && trimTarget[0] === '#') {
      trimTarget = trimTarget.substr(1);
    }
    const targetIDs = trimTarget.split('__');

    // Find the panel that matches the supplied slug ID, or just quit now.
    const targetID = targetIDs[0];
    const panel = document.querySelector(`.accordion-panel[data-slug="${targetID}"]`);
    if (!panel) {
      return;
    }

    // If this is a nested child panel, first expand its parent.
    const parentPanel = panel.parentNode.closest('.accordion-panel');
    this.activatePanel(parentPanel, true);

    // Now expand the panel itself.
    this.activatePanel(panel, true);

    // Calculate the height of any sticky bars to avoid when scrolling to a given section.
    this.scrollOffset = SCROLL_OFFSET_PADDING;
    const stickyEls = document.querySelectorAll('.spa-sticky');
    Array.prototype.forEach.call(stickyEls, (el) => {
      this.scrollOffset += el.offsetHeight;
    });

    // Scroll to the indicated section, but only if we're down to a single ID.
    if (targetIDs.length === 1) {
      setTimeout(() => {
        const targetEl = document.querySelector(`[data-slug="${targetIDs[0]}"]`);
        this.scrollTo(targetEl);
      }, PANEL_ANIMATION_LENGTH);
    }

    // If there is a second part to the supplied target, treat it the same as the first.
    if (targetIDs.length > 1 && targetIDs[1]) {
      this.activateSection(targetIDs[1]);
    }
  }

  /**
   * Shows/hides panels based on `accordion-type` and `one-at-a-time` data options.
   * @param {object} target - The panel to be activated, by `panel-header` link or a parent.
   * @param {boolean} forceState - Optional. When set, specifies the panel's open/closed state.
   * @returns {void}
   */
  activatePanel(target, forceState) {
    if (!target) {
      return;
    }

    // Focus in on the `panel-header` link, regardless of what we're passed.
    let item = target;
    if (!item.classList.contains('accordion-tab') && !item.classList.contains('panel-header')) {
      item = item.querySelector('.panel-header');
    }
    if (!item) {
      return;
    }

    // Ensure this panel is properly enclosed in an `accordion-wrapper`.
    const parent = item.closest('.accordion-wrapper');
    if (!parent) {
      return;
    }

    // Checking for proper panel/tab structure. Not an issue if the helper is used.
    const panelID = item.getAttribute('data-panel');
    const panel = parent.querySelector(`#${panelID}`);
    const tab = parent.querySelector(`.accordion-tab[data-panel=${panelID}]`);
    if (!panel || !tab) {
      return;
    }

    // Don't do anything when the user clicks an active tab.
    const alreadyActive = panel.classList.contains('active');
    const accordionType = parent.getAttribute('data-accordion-type');
    if (alreadyActive && accordionType === 'tabs') {
      const tabStyle = window.getComputedStyle(tab);
      if (tabStyle && tabStyle.visibility !== 'hidden') {
        return;
      }
    }

    // If the panel is already active or being forced closed, collapse it.
    if (alreadyActive || forceState === false) {
      // Nothing to do if the forced panel is already in the desired state.
      if ((!alreadyActive && forceState === false) || forceState === true) {
        return;
      }

      this.setActiveState(panel, false);
      this.setActiveState(tab, false);
      this.updateAccordionGroupState(parent);

      spartaEventsUtility.pub(EVENTS.ACCORDION_ITEM_HIDE, {
        accordion: parent,
        item: panel,
      });

      return;
    }

    // Otherwise, if the panel is not already active, expand it.
    // Start by closing any other open panels if necessary.
    const oneAtATime = (parent.getAttribute('data-one-at-a-time') === 'true');
    if (oneAtATime || accordionType === 'tabs') {
      // Close any other open panels/tabs.
      const els = document.querySelectorAll(`
        #${parent.id} > .accordion-panels > .accordion-panel,
        #${parent.id} > .accordion-tablist > .accordion-tab
      `);

      Array.prototype.forEach.call(els, (el) => {
        // Skip over the current panel/tab to avoid closing it unnecessarily.
        if (el === panel || el === tab) {
          return;
        }

        this.setActiveState(el, false);
      });
    }

    // Then open the necessary panel/tab.
    this.setActiveState(panel, true, true);
    this.setActiveState(tab, true);
    this.updateAccordionGroupState(parent);

    spartaEventsUtility.pub(EVENTS.ACCORDION_ITEM_SHOW, {
      accordion: parent,
      item: panel,
    });
  }

  /**
   * Shows panels based on `accordion-type` and `one-at-a-time` options.
   * @param {object} link - The show/hide link triggering the event.
   * @param {boolean} overrideConfigurations - Indicates if the `accordion-type` and `one-at-a-time`
   *   configuration settings should be ignored when showing the panels.
   * @returns {void}
   */
  showAllPanels(link, overrideConfigurations) {
    // Grab parent `accordion-wrapper` and hard set options to trigger CSS.
    const parent = link.closest('.accordion-wrapper');
    const collapseAllLink = (parent ? parent.querySelector('.accordion-collapse-all-link') : null );

    if (overrideConfigurations) {
      parent.setAttribute('data-accordion-type', 'accordion');
      parent.setAttribute('data-one-at-a-time', '');
    }

    const parentDataAttributes = parent.dataset;

    // Show all panels, if the accordion group is configured to support it
    if (
      parentDataAttributes.accordionType === 'accordion'
      && !parentDataAttributes.oneAtATime
    ) {
      // Grab all panels and apply the active state.
      const panels = parent.querySelectorAll(`
        #${parent.id} > .accordion-panels > .accordion-panel
      `);
      Array.prototype.forEach.call(panels, el => this.setActiveState(el, true));
      this.updateAccordionGroupState(parent);

      spartaEventsUtility.pub(EVENTS.ACCORDION_SHOW_ALL, {});

      // update focus state with delay due to open/close functions firing for each item event
      if (collapseAllLink) {
        setTimeout(() => {
          collapseAllLink.focus();
        }, 150);
      }
    }
  }

  /**
   * Shows panels and sub panels.
   * @param {Object} showHideLink The show/hide link triggering the event.
   * @returns {void}
   */
  exposeAllPanels(showHideLink) {
    // Grab parent `accordion-wrapper` and hard set options to trigger CSS.
    const parent = showHideLink.closest('.accordion-wrapper');
    const parentDataAttributes = parent.dataset;

    // Show all panels, if the accordion group is configured to support it
    if (parentDataAttributes.accordionType === 'accordion') {
      // Grab all panels and apply the active state.
      const panels = parent.querySelectorAll(`
        #${parent.id} > .accordion-panels > .accordion-panel
      `);
      Array.prototype.forEach.call(panels, el => this.setActiveState(el, true));
      this.updateAccordionGroupState(parent);

      const subPanels = parent.querySelectorAll('.show-all-text');
      Array.prototype.forEach.call(subPanels, (showAllLink) => {
        const content = showAllLink.textContent.toLowerCase();
        if (content.includes('show')) {
          this.togglePanels(showAllLink);
        }
      });
    }
  }

  /**
   * Hides panels based on `accordion-type` and `one-at-a-time` options.
   * @param {Object} link - The show/hide link triggering the event.
   * @returns {void}
   */
  hideAllPanels(link) {
    // Grab parent `accordion-wrapper` and hard set options to trigger CSS.
    const parent = link.closest('.accordion-wrapper');
    const parentDataAttributes = parent.dataset;
    const expandAllLink = (parent ? parent.querySelector('.accordion-expand-all-link') : null);

    // Show all panels, if the accordion group is configured to support it.
    if (
      parentDataAttributes.accordionType === 'accordion'
      && !parentDataAttributes.oneAtATime
    ) {
      // Grab all panels and apply the active state.
      const panels = parent.querySelectorAll(`
        #${parent.id} > .accordion-panels > .accordion-panel
      `);
      Array.prototype.forEach.call(panels, el => this.setActiveState(el, false));
      this.updateAccordionGroupState(parent);

      spartaEventsUtility.pub(EVENTS.ACCORDION_HIDE_ALL, {});
    }

    // update focus state with delay due to open/close functions firing for each item event
    if (expandAllLink) {
      setTimeout(() => {
        expandAllLink.focus();
      }, 150);
    }
  }

  /**
   * Shows or hides panels depending on which is needed.
   * @param {object} accordionGroup - The show/hide link triggering the event.
   * @returns {void}
   */
  updateAccordionGroupState(accordionGroup) {
    const groupClasses = accordionGroup.classList;

    const accordionsAllOpenClass = 'all-panels-open';
    const accordionsAllClosedClass = 'all-panels-closed';

    setTimeout(() => {
      const panelQuery = `#${accordionGroup.id} > .accordion-panels > .accordion-panel`;
      const panelCount = accordionGroup.querySelectorAll(panelQuery).length;
      const activePanelCount = accordionGroup.querySelectorAll(`${panelQuery}.active`).length;

      groupClasses.remove(accordionsAllOpenClass);
      groupClasses.remove(accordionsAllClosedClass);

      if (activePanelCount === 0) {
        groupClasses.add(accordionsAllClosedClass);
      } else if (activePanelCount === panelCount) {
        groupClasses.add(accordionsAllOpenClass);
      }
    }, 20);
  }

  /**
   * Shows or hides panels depending on which is needed.
   * @param {object} link - The show/hide link triggering the event.
   * @returns {void}
   */
  togglePanels(link) {
    const expanded = (link.getAttribute('data-expanded') === 'true');

    // Grab parent `accordion-wrapper` and hard set options to trigger CSS.
    const parent = link.closest('.accordion-wrapper');
    if (!expanded) {
      parent.setAttribute('data-accordion-type', 'accordion');
      parent.setAttribute('data-one-at-a-time', '');
    }

    // Switch the link's expanded state and alternate text.
    const modLink = link;
    const altText = link.getAttribute('data-alt-text') || 'Hide all';
    modLink.setAttribute('data-expanded', (expanded ? '' : 'true'));
    modLink.setAttribute('data-alt-text', link.text);
    modLink.text = altText;

    // Grab all panels and toggle the active state.
    const panels = parent.querySelectorAll('.accordion-panel');
    Array.prototype.forEach.call(panels, el => this.setActiveState(el, !expanded));
  }

  /**
   * Sets the `ada-hidden` tab message ("open" or "closed").
   * @param {object} el - The page element to be marked as "open" or "closed".
   * @param {boolean} active - Determines whether the element is active or not.
   * @returns {void}
   */
  setHiddenStateText(el, active) {
    // Find either the tab or panel header that will contain open/closed text.
    const panelHeader = el.querySelector('.panel-header');
    const headerEl = panelHeader || el;
    if (!headerEl) {
      return;
    }

    // Find the ADA-hidden text field to be used to show the open/closed state of the panel.
    const hiddenEl = headerEl.querySelector('.ada-hidden');
    if (!hiddenEl) {
      return;
    }

    // Add some open/closed state text for screenreaders.
    hiddenEl.textContent = active ? ', open' : ', closed';
  }

  /**
   * Sets the `active` class and other state markers for ADA compliance.
   * @param {object} el - The page element to be marked as "active" (or not).
   * @param {boolean} active - Determines whether the element is active.
   * @param {boolean} moveFocus - Optional. Determines the browser's focus should be shifted here.
   * @returns {void}
   */
  setActiveState(el, active, moveFocus = false) {
    // Update the ADA-hidden open/closed state text as early as possible.
    this.setHiddenStateText(el, active);

    // Determine whether we are talking about a tab or a panel.
    const inTabset = (el.parentNode.parentNode.getAttribute('data-accordion-type') === 'tabs');
    const panelEl = el.querySelector('.panel-content');

    // Cancel toggling a panel if it's already opening or closing. This allows for double-clicks.
    if (!inTabset && el.getAttribute('data-animating')) {
      return;
    }

    // Open or close the panel, or activate the tab.
    if (active) { // Show the panel.
      // Add the `active` class. A timeout is needed only for panel animation, not for tabs.
      if (inTabset) {
        el.classList.add('active');
      } else {
        el.setAttribute('data-animating', true);
        setTimeout(() => {
          el.classList.add('active');
        }, 0);
      }

      // The CSS animation needs fixed `max-height` values to work, so we have to clear that here.
      if (panelEl) {
        panelEl.style.maxHeight = null;
        setTimeout(() => {
          panelEl.style.maxHeight = 'none';
          el.removeAttribute('data-animating');
        }, PANEL_ANIMATION_LENGTH);
      }
    } else { // Hide the panel.
      // Remove the `active` class. A timeout is needed only for panel animation, not for tabs.
      if (inTabset) {
        el.classList.remove('active');
      } else {
        setTimeout(() => {
          el.classList.remove('active');
        }, 0);
        el.setAttribute('data-animating', true);
      }

      // This is needed for the closing CSS animation to work since we do the same for opening.
      if (panelEl) {
        panelEl.style.maxHeight = null;
        setTimeout(() => {
          el.removeAttribute('data-animating');
        }, PANEL_ANIMATION_LENGTH);
      }
    }

    // If this is a panel, hide or show its content for screenreaders.
    const isPanel = el.classList.contains('accordion-panel');

    if (isPanel) {
      const panelContent = el.querySelector('.panel-content');
      if (panelContent) {
        // Show or hide the content using the `hidden` attribute.
        if (active) {
          panelContent.removeAttribute('hidden');

          // Prompt screenreaders to begin reading this content when it is shown.
          if (moveFocus) {
            const childEl = panelContent.querySelector('.columns');
            if (childEl) {
              childEl.setAttribute('tabindex', -1);
              setTimeout(() => {
                childEl.focus();
                childEl.removeAttribute('tabindex');
              }, 0);
            }
          }
        } else if (inTabset) {
          panelContent.setAttribute('hidden', true);
        } else {
          // A timeout is needed only for panel animation, not for tabs.
          setTimeout(() => {
            panelContent.setAttribute('hidden', true);
          }, PANEL_ANIMATION_LENGTH + 1);
        }
      }
    }
  }

  /**
   * Checks for dynamic module content and appends it to the panel content.
   * @param {object} panel The panel element to be checked for dynamic module content.
   * @returns {void}
   */
  getModuleContent(panel) {
    const panelContent = panel.querySelector('[data-panel-content]');

    if (panelContent) {
      const moduleName = panelContent.getAttribute('data-panel-content');
      const moduleContent = document.querySelector(`[data-module="${moduleName}"]`);

      if (moduleContent) {
        panelContent.appendChild(moduleContent);

        moduleContent.style.display = 'block';
      } else {
        panelContent.innerHTML = STRINGS.NO_PANEL_CONTENT_ERROR;
      }
    }
  }

  /**
   * Shows one or more status icons in the accordion headers and tabs, including optional custom
   *   `ada-hidden` text for each icon. A default value is used if a custom value is not provided.
   * @param {object} targetAccordion - The accordion panel where the status icons need to be shown.
   * @param {array} aStatusIconConfigs - An array that determines which status icons to show in the
   *   accordion header/tab, as well as optional "ada-hidden" message overrides for each icon.
   *   Example to show an error icon and a success icon with a custom "ada-hidden" message:
   *     [
   *       { "iconClass": "error" },
   *       { "iconClass": "success", "adaText": ", is complete" }
   *     ]
   * @returns {void}
   */
  showStatusIcons(targetAccordion, aStatusIconConfigs = []) {
    aStatusIconConfigs.forEach((oIconConfig) => {
      const sIconClass = oIconConfig.iconClass;

      if (SUPPORTED_STATUS_ICONS.indexOf(sIconClass) !== -1) {
        const targetControls = this.findAccordionControls(targetAccordion);
        const customADAText = oIconConfig.adaText
          ? oIconConfig.adaText
          : this.content.statusIconADAText[sIconClass];

        targetControls.forEach((element) => {
          this.toggleStatusIconState(element, sIconClass, true, customADAText);
        });
      }
    });
  }

  /**
   * Hides one or more status icons in the accordion headers and tabs.
   * @param {object} targetAccordion - The accordion panel where the status icons need to be hidden.
   * @param {array} statusIcons - An array that determines which status icons to show in the
   *   accordion header/tab. Example to hide the error and success icons:
   *     [
   *       "error",
   *       "success"
   *     ]
   * @returns {void}
   */
  hideStatusIcons(targetAccordion, statusIcons = []) {
    statusIcons.forEach((sIcon) => {
      if (SUPPORTED_STATUS_ICONS.indexOf(sIcon) !== -1) {
        const targetControls = this.findAccordionControls(targetAccordion);

        targetControls.forEach((element) => {
          this.toggleStatusIconState(element, sIcon, false, this.content.statusIconADAText[sIcon]);
        });
      }
    });
  }

  /**
   * Returns the `panel-header` and `accordion-tab` elements for the target accordion.
   * @param {object} targetAccordion - The accordion panel whose control elements need to be
   *   identified.
   * @returns {void}
   */
  findAccordionControls(targetAccordion) {
    const accordionWrapper = targetAccordion.closest('.accordion-wrapper');
    const targetHeader = targetAccordion.querySelector(`
      .panel-header[data-panel='${targetAccordion.id}']
    `);
    const targetTab = accordionWrapper.querySelector(`
      .accordion-tab[data-panel='${targetAccordion.id}']
    `);
    const targetControls = [];

    targetControls.push(targetHeader);
    targetControls.push(targetTab);

    return targetControls;
  }

  /**
   * Hides one or more status icons in the accordion headers and tabs, based on configuraiton
   *   parameters.
   * @param {object} targetControl - The `panel-header` or `accordion-tab` control element where
   *   the status icon needs to be updated.
   * @param {string} statusIconClass - The class name for the status icon to be shown or hidden.
   * @param {boolean} showIcon - Determines whether the icon shold be shown or hidden.
   * @returns {void}
   */
  toggleStatusIconState(targetControl, statusIconClass, showIcon) {
    const targetADA = targetControl.querySelector(`.panel-status-icon.${statusIconClass}`);

    if (showIcon) {
      targetControl.classList.add(statusIconClass);
      targetADA.setAttribute('aria-label', targetADA.getAttribute('data-label'));
    } else {
      targetControl.classList.remove(statusIconClass);
      targetADA.removeAttribute('aria-label');
    }
  }

  /**
   * Sets up initial event listeners.
   * @returns {void}
   */
  addEventHandling() {
    const isSafari = (
      navigator.userAgent.indexOf('Safari') !== -1
      && navigator.userAgent.indexOf('Chrome') === -1
    );
    const isEdge = (navigator.userAgent.indexOf('Edge') !== -1);
    this.safariOrEdge = (isSafari || isEdge);
    this.scrollOffset = 0;

    // Hide all the "Hide all" links initially.
    const hideAllTextEls = document.querySelectorAll('.hide-all-text');
    Array.prototype.forEach.call(hideAllTextEls, (el) => {
      const elToHide = el;
      elToHide.style.display = 'none';
    });

    const toggleEls = document.querySelectorAll('.toggle-all-questions');
    Array.prototype.forEach.call(toggleEls, (el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();

        const thisEl = e.currentTarget;
        const wrapperEl = thisEl.parentNode;
        const isClosed = thisEl.classList.contains('spa-currently-closed');
        const showAllText = thisEl.querySelector('.show-all-text');
        const hideAllText = thisEl.querySelector('.hide-all-text');
        const toggleParent = thisEl.querySelector('.toggle-text').parentNode;
        const coremetricsJSON = toggleParent.getAttribute('data-coremetrics');
        const coremetricsData = coremetricsJSON ? JSON.parse(coremetricsJSON) : {};

        if (isClosed) {
          showAllText.style.display = 'none';
          hideAllText.style.display = '';

          toggleParent.setAttribute('id', coremetricsData.hide);
          thisEl.classList.remove('spa-currently-closed');
        } else {
          showAllText.style.display = '';
          hideAllText.style.display = 'none';

          toggleParent.setAttribute('id', coremetricsData.show);
          thisEl.classList.add('spa-currently-closed');
        }

        const allItems = wrapperEl.querySelectorAll('.spa-accordion-item');
        Array.prototype.forEach.call(allItems, (item) => {
          const isActive = item.classList.contains('active');
          if ((isClosed && !isActive) || (!isClosed && isActive)) {
            item.querySelector('.panel-header').click();
          }
        });
      });
    });

    const linkEls = document.querySelectorAll('.spa-accordion-link');
    Array.prototype.forEach.call(linkEls, (el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();

        const thisEl = e.currentTarget;
        const isOpen = thisEl.parentNode.classList.contains('active');
        const coremetricsJSON = thisEl.getAttribute('data-coremetrics');
        const coremetricsData = coremetricsJSON ? JSON.parse(coremetricsJSON) : {};

        // Slight delay of half a second before updating the ID.
        setTimeout(() => {
          if (isOpen) {
            thisEl.setAttribute('id', coremetricsData.enabled);
          } else {
            thisEl.setAttribute('id', coremetricsData.disabled);
          }
        }, 500);
      });
    });
  }

  /**
   * Scrolls to a given element by slug ID, accounting for sticky bar height.
   * @param {string} target Page element to be scrolled into view.
   * @returns {void}
   */
  scrollTo(target) {
    // Ensure we have a valid page element to scroll to.
    if (!target) {
      return;
    }

    // Safari and Edge don't use a different `scrollTop` value.
    const currentScroll = this.safariOrEdge
      ? document.body.scrollTop
      : document.documentElement.scrollTop;

    // Figure out the particular spot we want to scroll to.
    const targetScroll = Math.max(
      0,
      target.getBoundingClientRect().top + currentScroll - this.scrollOffset,
    );

    // Move closer to the target.
    const newScroll = (currentScroll * 0.3) + (targetScroll * 0.7);

    // Set the new scroll position (allowing for browser differences).
    if (this.safariOrEdge) {
      document.body.scrollTop = newScroll;
    } else {
      document.documentElement.scrollTop = newScroll;
    }

    const isAtBottom = (window.innerHeight + window.scrollY) >= document.body.offsetHeight;

    // If we're not there yet, keep going. This creates the smooth scroll animation.
    if (Math.abs(currentScroll - targetScroll) > 2 && !isAtBottom) {
      setTimeout(() => {
        this.scrollTo(target);
      }, 100);
    }
  }


  /**
   * On top level collapse/show control click toggle any innerPanel accordions
   * @param {object || string} accordionElement - DOM element or ID of accordion to set control of
   */
  expandCollapseInnerPanels(accordionElement) {
    // did we get an ID or element
    let wrapper = accordionElement;
    if (typeof wrapper === 'string') {
      wrapper = document.getElementById(accordionElement);
    }

    // kill check against instance
    if (instance[wrapper.id].allowExpandCollapseInnerPanels === false) {
      console.log('expandCollapseInnerPanels is already set on this instance: ', wrapper.id, instance[wrapper.id])
      return false;
    }

    if (wrapper) {
      const showAllTrigger = wrapper.querySelector('.accordion-expand-all-link');
      const collapseAllTrigger = wrapper.querySelector('.accordion-collapse-all-link');
      const allInnerPanels = [...wrapper.querySelectorAll('.innerPanels .panel-header')];
      const allInnerPanelsControls = [...wrapper.querySelectorAll('.innerPanels .accordion-group-control')];
      
      // open any and all children accordions
      showAllTrigger.addEventListener('click', (event) => {     
        const allChildrenShowAllTriggers = [...wrapper.querySelectorAll('.panel-header')];
        allChildrenShowAllTriggers.forEach( (element) => {
          this.activatePanel(element, true);
        });      
      });
      
      // collapse any and all children accordions
      collapseAllTrigger.addEventListener('click', (event) => {
        const allChildrenCollapseAllTriggers = [...wrapper.querySelectorAll('.panel-header')];
        allChildrenCollapseAllTriggers.forEach( (element) => {
          this.activatePanel(element, false);
        });
      });

      // create listener to update top level control if not all inner panels are visible/hidden
      allInnerPanels.concat(allInnerPanelsControls).forEach( (element) => {
        element.addEventListener('click', () => {
          wrapper.classList.remove('all-panels-open');
          wrapper.classList.remove('all-panels-closed');
        });
      });
    }

    // prevent future events from binding
    instance[wrapper.id].allowExpandCollapseInnerPanels = false;
  }

  /**
   * @returns {object} A collection of namespaced event strings for this utility.
   */
  get EVENTS() {
    return EVENTS;
  }
}

export default new SpartaAccordion();
